import * as React from "react";
import "hammerjs";
import {
  Chart,
  ChartArea,
  ChartCategoryAxis,
  ChartCategoryAxisItem,
  ChartCategoryAxisItemProps,
  ChartLegend,
  ChartProps,
  ChartSeries,
  ChartSeriesItem,
  ChartSeriesItemProps,
  ChartSeriesMarkers,
  ChartTooltip,
  ChartValueAxis,
  ChartValueAxisItem,
  ChartValueAxisItemProps,
  MarkersVisualArgs,
  SharedTooltipContext,
  ValueAxisNotes,
} from "@progress/kendo-react-charts";
import {
  defaultCategoryAxisFormatter,
  defaultValueAxisFormatter,
} from "../../utils/defaultFormatters";
import {
  TimeSeriesSharedTooltipRenderer,
  TooltipSettings,
} from "../TimeSeriesSharedTooltip/TimeSeriesSharedTooltipRenderer";
import {
  getDefaultCategoryAxisStyling,
  getDefaultValueAxisProps,
} from "../../utils/chartDefaults";
import { TimeSeriesChartItem } from "../TimeSeriesStackedChart/TimeSeriesStackedChart";
import { drawing } from "@progress/kendo-drawing";
import { CategoryAxisNotes } from "@progress/kendo-react-charts/dist/npm/option-types/category-axis-item/notes.interface";

type SharedTooltipRenderType = (
  info: SharedTooltipContext,
  valueAxisFormatter?: (e?: number) => string,
  categoryAxisFormatter?: (e?: string) => string,
  settings?: TooltipSettings
) => React.ReactNode;

export interface TimeSeriesLineChartProps extends ChartProps {
  items: TimeSeriesChartItem[];
  sharedTooltipRender?: SharedTooltipRenderType;
  valueAxisFormatter?(e?: number): string;
  categoryAxisFormatter?(e?: string): string;
  tooltipCategoryAxisFormatter?(e?: string): string;
  chartStyle?: React.CSSProperties;
  axisCrossingValue?: number;
  baseUnit?: "days" | "months" | "years" | "weeks";
  baseUnitStep?: number | "auto";
  maxDivisions?: number;
  lineStyle?: "smooth" | "normal" | "step";
  markers?: boolean;
  min?: number;
  max?: number;
  aggregate?: any;
  markerRenderer?: (e: MarkersVisualArgs) => drawing.Element;
  categoryAxisNotes?: CategoryAxisNotes;
  valueAxisNotes?: ValueAxisNotes;
  tooltipSettings?: TooltipSettings;
  chartCategoryAxisItemProps?: ChartCategoryAxisItemProps;
  chartValueAxisItemProps?: ChartValueAxisItemProps;
  seriesType?: "line" | "area" | "candlestick";
  isPdf?: boolean;
  height?: number;
  width?: number;
  chartSeriesItemProps?: ChartSeriesItemProps;
}

const DEFAULT_MAX_DIVISIONS = 12;
const TimeSeriesLineChart = ({
  items,
  sharedTooltipRender = TimeSeriesSharedTooltipRenderer,
  valueAxisFormatter = defaultValueAxisFormatter,
  categoryAxisFormatter = defaultCategoryAxisFormatter,
  tooltipCategoryAxisFormatter,
  chartStyle,
  axisCrossingValue = 0,
  baseUnit = "days",
  maxDivisions = DEFAULT_MAX_DIVISIONS,
  lineStyle = "normal",
  markers = false,
  max,
  min,
  aggregate,
  markerRenderer,
  categoryAxisNotes,
  valueAxisNotes,
  tooltipSettings,
  chartCategoryAxisItemProps,
  chartValueAxisItemProps,
  seriesType = "line",
  isPdf = false,
  height,
  width,
  chartSeriesItemProps,
  ...props
}: TimeSeriesLineChartProps) => {
  return (
    // @ts-ignore - bad type missing children
    <Chart style={{ ...chartStyle, height, width }} {...props}>
      <ChartArea background={"transparent"} height={height} width={width} />
      <ChartLegend visible={false} />
      {typeof sharedTooltipRender === "function" && (
        <ChartTooltip
          opacity={0.9}
          color={"var(--tooltip-color)"}
          background={"var(--tooltip-background-color)"}
          render={(info: any) =>
            sharedTooltipRender(
              info as SharedTooltipContext,
              valueAxisFormatter,
              tooltipCategoryAxisFormatter
                ? tooltipCategoryAxisFormatter
                : categoryAxisFormatter,
              tooltipSettings
            )
          }
          shared={true}
        />
      )}
      <ChartSeries>
        {items.map((item) => (
          <ChartSeriesItem
            key={item.category}
            type={(item.seriesType as any) || seriesType}
            categoryField="date"
            dashType={item.dashType || "solid"}
            style={item.lineStyle || lineStyle}
            line={{
              style: item.lineStyle || lineStyle,
            }}
            data={item.entries}
            name={item.category}
            markers={{
              visible: markers,
            }}
            color={item.color}
            width={item.lineWidth || 2}
            border={{
              width: 1,
            }}
            {...chartSeriesItemProps}
          >
            {markerRenderer && <ChartSeriesMarkers visual={markerRenderer} />}
          </ChartSeriesItem>
        ))}
      </ChartSeries>
      <ChartCategoryAxis>
        <ChartCategoryAxisItem
          {...getDefaultCategoryAxisStyling({
            majorGridLines: {
              color: "transparent",
            },
            notes: categoryAxisNotes,
            line: { color: "transparent" },
            labels: {
              color: "var(--ghost-color)",
              content: (e: any) => categoryAxisFormatter(e.value),
            },
            maxDivisions,
            baseUnit,
            ...chartCategoryAxisItemProps,
          })}
        />
      </ChartCategoryAxis>
      <ChartValueAxis>
        <ChartValueAxisItem
          {...getDefaultValueAxisProps({
            majorGridLines: {
              color: isPdf ? undefined : "var(--muted-text-color)",
            },
            notes: valueAxisNotes,
            min,
            max,
            axisCrossingValue,
            labels: {
              color: "var(--ghost-color)",
              content: (e: any) => valueAxisFormatter(e.value),
            },
            ...chartValueAxisItemProps,
          })}
        />
      </ChartValueAxis>
    </Chart>
  );
};

export default TimeSeriesLineChart;
